// Announcements — design AnnouncementsScreen top grid: an Announcements card
// (MorphTabs All/Unread + scope-tinted glass rows → detail) beside a Calendar
// card (current month, deadline dots, day detail; mirrors the dashboard
// calendar, no note-taking). Prefix: qbw-.
export const announcementListViewStyles = `
  .qbw-root { margin: 0; }
  .qbw-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-10);
  }

  /* Top grid: announcements card + calendar */
  .qbw-grid-top {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    gap: var(--space-5);
    /* Each column sizes to its own content — the calendar card must not
       stretch to the (taller) announcement list's height. */
    align-items: start;
  }
  .qbw-card-base {
    border-radius: var(--rad-catchup-xlarge);
    background: var(--glass-flat-bg);
    -webkit-backdrop-filter: var(--glass-flat-filter);
    backdrop-filter: var(--glass-flat-filter);
    border: 1px solid var(--glass-flat-border);
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
  }
  /* No background card — the selection tab + announcement rows sit directly on
     the page; each row carries its own glass surface. */
  .qbw-anncard {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    min-width: 0;
  }
  .qbw-anncard-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  /* "Mark all read" is now the shared library SecondaryButton. */
  /* Paginated list (page-travel), no inner scroll. */
  .qbw-rows--panel { flex: 0 0 auto; }
  .qbw-pager { margin-top: var(--space-2); }

  /* Calendar card shell — hosts the shared library <Calendar/> primitive (cal- prefix) */
  .qbw-cal {
    border-radius: var(--rad-catchup-xlarge);
    background: var(--glass-flat-bg);
    -webkit-backdrop-filter: var(--glass-flat-filter);
    backdrop-filter: var(--glass-flat-filter);
    border: 1px solid var(--glass-flat-border);
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* Rows */
  .qbw-rows { display: flex; flex-direction: column; gap: var(--space-3); }
  .qbw-row {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: var(--rad-catchup-large);
    /* Flat near-opaque fill instead of --glass-flat-bg + backdrop-filter: this row
       is rendered once per announcement (per-item, unbounded list + the dashboard
       widget) and a per-item blur(24px) re-composites on every scroll → "renders
       after"/jank. Mirrors the earlier etl-row / etl-card flattening. */
    background: var(--glass-flat-solid);
    border: 1px solid var(--glass-flat-border);
    box-shadow: var(--glass-flat-edge), var(--glass-flat-float-rest);
    transition: transform var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .qbw-row:hover { transform: translateY(-2px); box-shadow: var(--glass-flat-edge), var(--glass-flat-float-hover); }
  /* An expanded row stays put (no hover lift) so the revealed detail never jumps. */
  .qbw-row.is-open, .qbw-row.is-open:hover { transform: none; box-shadow: var(--glass-flat-edge), var(--glass-flat-float-hover); }
  .qbw-row.is-unread { background: rgba(255, 255, 255, 0.96); }
  /* Clickable summary (the collapsed row surface) */
  .qbw-row-summary {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
    text-align: left;
    cursor: pointer;
    font-family: var(--font-sans);
    padding: var(--space-4);
    background: transparent;
    border: none;
    color: inherit;
    border-radius: var(--rad-catchup-large);
  }
  /* Inline detail revealed on expand */
  .qbw-row-detail {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: 0 var(--space-4) var(--space-4);
  }
  .qbw-row-detail-divider { height: 1px; background: rgba(15, 72, 89, 0.1); }
  .qbw-row-fulltext { font-size: var(--fs-base); color: var(--text-secondary); line-height: 1.65; white-space: pre-wrap; }
  .qbw-row-file { display: flex; align-items: center; gap: var(--space-3); }
  .qbw-row-tile {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: var(--rad-catchup-medium);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.55);
  }
  .qbw-row-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .qbw-row-dot {
    position: absolute;
    top: 14px;
    right: 38px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--accent-coral);
    box-shadow: 0 0 0 3px var(--accent-coral-bg);
  }
  .qbw-row-title { font-size: var(--fs-lg); font-weight: 700; color: var(--text-primary); line-height: 1.3; }
  .qbw-row-text {
    font-size: var(--fs-base);
    color: var(--text-secondary);
    line-height: var(--leading-snug);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .qbw-row-meta { display: flex; align-items: center; gap: var(--space-2); font-size: var(--fs-md); font-weight: 600; margin-top: 2px; }
  .qbw-row-date { color: var(--text-muted); }
  .qbw-row-sep { color: var(--text-muted); }
  .qbw-row-scope { font-weight: 700; }
  .qbw-row-chev { align-self: center; flex-shrink: 0; font-size: 24px; line-height: 1; color: var(--text-muted); transition: transform var(--duration-base) var(--ease-standard); }
  .qbw-row.is-open .qbw-row-chev { transform: rotate(90deg); }

  /* File preview (inside the inline detail) */
  .qbw-file-preview { width: 200px; }

  /* Minimized (dashboard) variant */
  .qbw-full { width: 100%; }
  .qbw-min-content { margin-top: 1rem; }
  .qbw-min-divider { height: 1px; background: var(--glass-flat-border); margin: 0.5rem 0; }
  .qbw-min-row { display: flex; flex-direction: row; justify-content: space-between; column-gap: 0.5rem; }
  .qbw-min-dot {
    width: 0.5rem;
    height: 0.5rem;
    background-color: var(--catchup-red);
    border-radius: var(--rad-catchup-full);
    margin-top: 0.5rem;
  }
  .qbw-min-col { flex: 1 1 0%; display: flex; flex-direction: column; }
  .qbw-min-name-row { display: flex; flex-direction: row; align-items: flex-end; }
  .qbw-min-name-unread { font-weight: 600; }

  @media (max-width: 980px) {
    .qbw-grid-top { grid-template-columns: 1fr; }
  }
  @media (max-width: 680px) {
    .qbw-row-chev { display: none; }
    .qbw-row-dot { right: 16px; }
  }
`;
